import React, { ComponentType, FC } from 'react';

import styled from '@emotion/styled';
import { DesignsToken } from '@repo/tokens-studio';
import { NamespaceKeys, useTranslations } from 'next-intl';

import { BadgeCode, badgeDescriptionKey } from '@web/features/badges/catalog';

import { ITooltipProps, Tooltip } from '../Tooltip';

import Placeholder from './assets/_placeholder.svg';

// Per-code asset overrides. When a real SVG lands for a code, add it here:
//   import HOLDER_I_Svg from './assets/HOLDER_I.svg';
//   const OVERRIDES = { [BadgeCode.HOLDER_I]: HOLDER_I_Svg, ... };
// Anything not in this map falls back to the shared placeholder.
const OVERRIDES: Partial<Record<BadgeCode, ComponentType>> = {};

const BadgeContainer = styled.div<{
  size?: keyof DesignsToken['size'];
  disabled?: boolean;
}>`
  height: ${({ theme, size }) => size && theme.size[size]};
  width: ${({ theme, size }) => size && theme.size[size]};
  cursor: pointer;
  transition: transform 150ms;
  ${({ disabled }) =>
    disabled
      ? `
        opacity: 0.38;
        filter: grayscale(1);
    `
      : ''}

  svg {
    width: 100%;
    height: 100%;
  }

  :hover {
    transform: scale(1.05) rotate(-10deg);
  }

  :active {
    transform: scale(1) rotate(0deg);
  }
`;

export interface IBadgeProps {
  size?: keyof DesignsToken['size'];
  badge: BadgeCode;
  disabled?: boolean;
  tooltip?: Omit<ITooltipProps, 'content' | 'children'>;
  className?: string;
}

export const Badge: FC<IBadgeProps> = ({
  badge,
  size,
  disabled,
  tooltip,
  className,
}) => {
  const t = useTranslations();

  const Svg = OVERRIDES[badge] ?? Placeholder;
  const tooltipContent = t(
    badgeDescriptionKey(badge) as NamespaceKeys<string, string>,
  );

  return (
    <Tooltip content={tooltipContent} {...tooltip}>
      <BadgeContainer size={size} disabled={disabled} className={className}>
        <Svg />
      </BadgeContainer>
    </Tooltip>
  );
};
